import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, forkJoin, of, startWith, Subject, take, takeUntil } from 'rxjs';
import { AppService } from '@app/app.service';
import { SystemMessageService } from '@app/core/services/system-message.service';
import { TranslateConfigService } from '@app/core/translate-config.service';
import { AuthService } from '@app/core/auth/auth.service';
import { ChannelsApi } from '@api/services/channels-api';
import { Customer } from '@api/index';
import { ChannelFormGroup } from '../model/channel.model';
import { Store } from '@ngrx/store';
import { selectUserCustomers } from '@app/store/user/user.selectors';
import { IdName } from '@api/models/idname';
import { apiCallCatchError } from '@app/shared/utils/utils';
import { BIZLAB_LATITUDE, BIZLAB_LONGITUDE } from '@app/shared/constants/bizLab-coordinates';
import { ChannelDetails } from '@api/models/channels';
import { UserRoleEnum } from '@app/admin/user/model/user.model';
import { DateTime } from 'luxon';

@Component({
  selector: 'flow-channel-create',
  templateUrl: './channel-create.component.html',
  styleUrls: ['./channel-create.component.scss'],
})
export class ChannelCreateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isAdmin: boolean;
  channelId: number;
  channel: ChannelDetails;
  customers: Customer[];
  form: FormGroup<ChannelFormGroup>;
  lineOfBusinessOptions: IdName[] = [];
  bizLabLatitude = BIZLAB_LATITUDE;
  bizLabLongitude = BIZLAB_LONGITUDE;
  currentLocalDate = DateTime.local().toISODate();

  get customerId() { return this.form.get('customerId')!; }
  get allowTraining() { return this.form.get('allowTraining')!; }
  get isWeatherCollectionEnabled() { return this.form.get('isWeatherCollectionEnabled')!; }
  get isForecastEnabled() { return this.form.get('isForecastEnabled')!; }
  get hasClosedDays() { return this.form.get('hasClosedDays')!; }

  constructor(
    private channelsApi: ChannelsApi,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private systemMessageService: SystemMessageService,
    private translateConfigService: TranslateConfigService,
    private authService: AuthService,
    private store: Store,
    private appService: AppService
  ) { }

  ngOnInit(): void {
    this.appService.hasToolbarFormActions = true;
    this.channelId = +this.activatedRoute.snapshot.params['id'] || null;
    const user = this.authService.userData;
    this.isAdmin = user.roleId === UserRoleEnum.Admin && !user.currentCustomerId;

    this.initForm();
    this.getData();
  }

  getData(): void {
    forkJoin([
      this.channelId ? apiCallCatchError(this.channelsApi.getChannelById(this.channelId)) : of(null),
      this.store.select(selectUserCustomers).pipe(
        filter(customers => customers && customers.length > 0),
        take(1)
      ),
      this.channelsApi.getLineOfBusinessList(),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([channel, customers, lineOfBusinessList]) => {
        this.channel = channel;
        this.customers = customers;
        this.lineOfBusinessOptions = lineOfBusinessList.map(item => ({
          id: item.id,
          name: `createChannel.lineOfBusiness.${item.name}`,
        }));
        this.initForm(channel);
      });
  }

  onSaveClick(): void {
    if (!this.form.valid) {
      return;
    }
    const body = {
      ...this.channel,
      ...this.form.getRawValue(),
      currentLocalDate: this.currentLocalDate
    };

    const mode = this.channelId ? 'editChannel' : 'saveChannel';

    this.channelsApi[mode](body)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.systemMessageService.success(this.translateConfigService.instant('notifications.success.channelCreate', this.form.value.name));
        this.router.navigate(['/channels']);
      });
  }

  private initForm(channel?: ChannelDetails): void {
    const initialAllowTraining = channel ? channel.allowTraining ?? !!channel.isForecastEnabled : true;
    const initialWeatherCollectionEnabled = channel ? channel.isWeatherCollectionEnabled ?? !!channel.isForecastEnabled : true;

    this.form = this.formBuilder.group({
      id: this.channelId,
      name: [channel?.name, Validators.required],
      city: [channel?.city, Validators.required],
      address: [channel?.address, Validators.required],
      latitude: [channel?.latitude ?? this.bizLabLatitude, Validators.required],
      longitude: [channel?.longitude ?? this.bizLabLongitude, Validators.required],
      postalCode: [channel?.postalCode, Validators.required],
      customerId: [channel?.customerId, Validators.required],
      lineOfBusiness: [channel?.lineOfBusiness.id, Validators.required],
      allowTraining: [initialAllowTraining],
      isWeatherCollectionEnabled: [initialWeatherCollectionEnabled],
      isForecastEnabled: [!!channel?.isForecastEnabled],
      hasClosedDays: [!!channel?.hasClosedDays],
    });


    if (this.channelId && !channel) {
      this.form.disable();
    } else {
      this.form.enable();
    }

    this.hasClosedDays.disable();

    if (this.channelId) {
      this.customerId.disable();
    }

    if (this.isAdmin) {
      this.setForecastDependencies();
      this.setAllowTrainingDependencies();
    }
  }

  private setForecastDependencies(): void {
    this.isForecastEnabled.valueChanges.pipe(startWith(this.isForecastEnabled.value), takeUntil(this.destroy$)).subscribe({
      next: (checked: boolean) => {
        if (checked) {
          if (!this.allowTraining.value) {
            this.allowTraining.setValue(true, { emitEvent: false });
          }
          if (!this.isWeatherCollectionEnabled.value) {
            this.isWeatherCollectionEnabled.setValue(true, { emitEvent: false });
          }

          this.hasClosedDays.enable({ emitEvent: false });

          this.isWeatherCollectionEnabled.disable({ emitEvent: false });
          this.allowTraining.disable({ emitEvent: false });
        } else {
          this.hasClosedDays.setValue(false, { emitEvent: false });
          this.hasClosedDays.disable({ emitEvent: false });

          this.allowTraining.enable({ emitEvent: false });
          this.isWeatherCollectionEnabled.enable({ emitEvent: false });

          if (this.allowTraining.value) {
            this.isWeatherCollectionEnabled.disable({ emitEvent: false });
          }
        }
      },
      error: () => {
        this.systemMessageService.error(this.translateConfigService.instant('notifications.error.genericError'));
      },
    });
  }

  setAllowTrainingDependencies(): void {
    this.allowTraining.valueChanges.pipe(startWith(this.allowTraining.value), takeUntil(this.destroy$)).subscribe({
      next: (allowTraining: boolean) => {
        if (allowTraining) {
          this.isWeatherCollectionEnabled.setValue(true, { emitEvent: false });
          this.isWeatherCollectionEnabled.disable({ emitEvent: false });
        } else {
          this.isWeatherCollectionEnabled.enable({ emitEvent: false });
        }
      },
      error: () => {
        this.systemMessageService.error(this.translateConfigService.instant('notifications.error.genericError'));
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
